// @ts-check
/* eslint-env browser */

/**
 * @typedef {Object} StepActionContext
 * @property {function(string): void} clickTab
 * @property {function(string): void} openSummary
 * @property {function(): boolean} getSkipFullscreen
 * @property {function(boolean): void} setSkipFullscreen
 */

export const TOUR_STEPS_MAP = {
  extraction: [
    {
      title: 'Extraction onboarding tour',
      tooltipClass: 'onboarding-tour--intro',
      intro: 'Welcome to the extraction page! This is an interactive demo to help you get started.<br><br>The next step will switch to fullscreen mode for a clearer view. You can press <kbd>Esc</kbd> at any time to exit fullscreen.'
    },
    { title: 'Full screen', element: "label[for='fullscreen']", intro: 'We recommend entering full screen mode for more space to work, everything you need to review, edit and export is available here.', position: 'left' },
    { title: 'PDF viewer', element: 'pdf-viewer', intro: 'Details can be checked against the original document.', position: 'left' },
    { title: 'Map options', element: 'tile-switcher', intro: 'Toggle between satellite and OS modes.', position: 'left' },
    { title: 'Tabs', element: '.govuk-tabs__list', intro: 'Document features are organised by tab.', position: 'right' },
    { title: 'TPO tab', element: '.govuk-tabs', intro: 'Text from the overall order is extracted and fields are pre-populated.', position: 'right' },
    { title: 'Direction tab', element: '.govuk-tabs', intro: 'Text from the direction is extracted and fields are pre-populated.', position: 'right' },
    { title: 'Maps tab', element: '.govuk-tabs', intro: 'Maps are extracted, geo-located and listed here. Not all extractions will include a map.', position: 'right' },
    { title: 'Map spatial editing', element: 'edit-controls', intro: 'Easily make adjustments to move, rotate and scale the map.', position: 'right' },
    { title: 'Map opacity', element: 'opacity-slider', intro: 'Opacity can be adjusted to ensure accuracy.', position: 'right' },
    { title: 'Mark maps as complete', element: 'map-overlay-item button[value="complete"]', intro: 'Once happy, mark as complete to track your progress.', position: 'right' },
    { title: 'Trees tab', element: '.govuk-tabs', intro: 'Trees are extracted and mapped spatially and listed here.', position: 'right' },
    { title: 'Trees spatial editing', element: 'edit-controls', intro: 'Easily make spatial adjustments to move trees.', position: 'right' },
    { title: 'Zones tab', element: '.govuk-tabs', intro: 'Zones are also extracted and mapped spatially and listed here.', position: 'right' },
    { title: 'Zones spatial editing', element: 'edit-controls', intro: 'Easily make spatial adjustments to move, rotate, cut, scale and add new zones.', position: 'right' },
    { title: 'Areas tab', element: '.govuk-tabs', intro: 'Areas are also extracted and mapped spatially and listed here.', position: 'right' },
    { title: 'Areas spatial editing', element: 'edit-controls', intro: 'Easily make spatial adjustments to move, rotate, cut, scale and add new areas.', position: 'right' },
    { title: 'Export data', element: '.map-container__export', intro: 'Download for import into your local GIS systems to add to existing layers or make new ones.<br><br>Choose CSV in the export dialog if you are looking for data that matches the schema for the Check and provide service.', position: 'right' }
  ],
  homepage: [
    {
      title: 'Homepage onboarding tour',
      tooltipClass: 'onboarding-tour--intro',
      intro: 'Welcome to the homepage! This is an interactive demo to help you get started.'
    },
    {
      title: 'Upload documents',
      element: '#upload-new-documents-button',
      position: 'top',
      intro: 'Upload documents to get started.'
    },
    {
      title: 'Extraction list',
      element: '.govuk-tabs',
      position: 'top',
      intro: 'Your extractions are listed here.<br><br>If you\'ve just started there may not be any extractions yet.<br><br>Extract is a shared workspace, you’ll see extracted documents from users across your organisation listed here.'
    },
    {
      title: 'Tabs',
      element: '.govuk-tabs__list',
      position: 'top',
      intro: 'Documents are organised by type.'
    },
    {
      title: 'Export selected data',
      element: '.extraction-list__export-selected-button',
      position: 'top',
      intro: 'Select one or more extractions, then click here to open export options for GeoJSON, Shapefile, GeoTIFF or CSV.'
    }
  ],
  upload: [
    {
      title: 'Upload onboarding tour',
      tooltipClass: 'onboarding-tour--intro',
      intro: 'Welcome to the upload page! This is an interactive demo to help you get started.'
    },
    {
      title: 'Document type',
      element: '.doc-type-fieldset',
      position: 'top',
      intro: 'Extract currently supports TPOs, Article 4s and conservation areas.<br><br>You can only upload one document type at a time.'
    },
    {
      title: 'Choose documents to upload',
      position: 'top',
      element: '.govuk-tabs',
      intro: 'Upload PDFs or images.<br><br>Multiple files can be uploaded at once.'
    },
    {
      title: 'Enter a URL',
      element: '.govuk-tabs',
      position: 'top',
      intro: 'You can also enter a URL to upload a document or documents.'
    },
    {
      title: 'Upload documents',
      element: '#upload-docs-button',
      position: 'top',
      intro: 'Upload documents to get started.'
    }
  ]
}

export const STEP_ACTIONS_MAP = {
  /**
   * @param {StepActionContext} context
   * @param {string} title
   */
  extraction: function (context, title) {
    const { setSkipFullscreen, getSkipFullscreen, clickTab, openSummary } = context

    switch (title) {
      case 'Extraction onboarding tour': {
        setSkipFullscreen(false) // reset so re-entering step 1 (via Back) clears any prior skip decision
        break
      }
      case 'Full screen': {
        /** @type {HTMLInputElement | null} */
        const fullscreenCheckbox = document.querySelector('input[type="checkbox"][id="fullscreen"]')

        // Detect skip-fullscreen via focused element as intro.js calls preventDefault() on Enter so gets missed from enter press of skip-fullscreen button.
        const activeElement = document.activeElement
        const userClickedSkipFullscreen = getSkipFullscreen() || Boolean(activeElement && activeElement.id === 'skip-fullscreen')
        if (fullscreenCheckbox) {
          if (!userClickedSkipFullscreen && !fullscreenCheckbox.checked) {
            fullscreenCheckbox.click()
          } else if (userClickedSkipFullscreen && fullscreenCheckbox.checked) {
            fullscreenCheckbox.click()
          }
          setSkipFullscreen(userClickedSkipFullscreen)
        }
        break
      }
      case 'TPO tab': {
        clickTab('#tab_treepreservationorders')
        openSummary('#treepreservationorders')
        break
      }
      case 'Direction tab': {
        clickTab('#tab_article4directions')
        openSummary('#article4directions')
        break
      }
      case 'Maps tab': {
        clickTab('#tab_maps')
        break
      }
      case 'Map spatial editing': {
        openSummary('map-overlay-item')
        break
      }
      case 'Mark maps as complete': {
        clickTab('#tab_maps')
        break
      }
      case 'Trees tab': {
        clickTab('#tab_trees')
        break
      }
      case 'Trees spatial editing': {
        openSummary('#trees')
        break
      }
      case 'Zones tab': {
        clickTab('#tab_zones')
        break
      }
      case 'Zones spatial editing': {
        openSummary('#zones')
        break
      }
      case 'Areas tab': {
        clickTab('#tab_area')
        clickTab('#tab_article4directionareas')
        break
      }
      case 'Areas spatial editing': {
        openSummary('#area')
        openSummary('#article4directionareas')
        break
      }
      default:
        break
    }
  },
  /**
   * @param {StepActionContext} context
   * @param {string} title
   */
  upload: function (context, title) {
    const { clickTab } = context
    switch (title) {
      case 'Enter a URL': {
        clickTab('#tab_url-tab')
        break
      }
      case 'Upload documents': {
        clickTab('#tab_upload-tab')
        break
      }
    }
  }
}

export const IS_STEP_AVAILABLE_MAP = {
  extraction: (title) => {
    switch (title) {
      case 'TPO tab':
        return !!document.querySelector('#tab_treepreservationorders')
      case 'Direction tab':
        return !!document.querySelector('#tab_article4directions')
      case 'Trees tab':
      case 'Trees spatial editing':
        return !!document.querySelector('#tab_trees')
      case 'Zones tab':
      case 'Zones spatial editing':
        return !!document.querySelector('#tab_zones')
      case 'Areas spatial editing':
      case 'Areas tab':
        return !!(document.querySelector('#tab_area') || document.querySelector('#tab_article4directionareas'))
      default:
        return true
    }
  },
  upload: () => true, // all steps are available
  homepage: () => true // all steps are available
}

export const TOUR_CONTAINER_MAP = {
  extraction: 'map-container',
  homepage: 'body',
  upload: 'body'
}
